import { createContext, useContext } from 'react';

import type { FieldDiff, SectionReviewDiff } from './types';

// Provides the collection block ID so item-level diff hooks can look up composite keys
// without threading blockId through every intermediate component.
export const CollectionBlockIdContext = createContext<string | null>(null);

export const SectionReviewContext = createContext<SectionReviewDiff | null>(null);

export function useBlockFieldDiff(blockId: string): FieldDiff | null {
  const diff = useContext(SectionReviewContext);
  if (!diff || diff.designChanged) return null;
  return diff.fieldDiffs.find((c) => c.fieldKey === blockId) ?? null;
}

export function useSectionFieldDiff(fieldKey: string): FieldDiff | null {
  const diff = useContext(SectionReviewContext);
  if (!diff || diff.designChanged) return null;
  return diff.fieldDiffs.find((c) => c.fieldKey === fieldKey) ?? null;
}

// Returns the diff for a specific collection item field (blockId:itemId:slot).
// Reads blockId from CollectionBlockIdContext if not provided directly.
export function useCollectionFieldDiff(itemId: string, slot: string): FieldDiff | null {
  const diff = useContext(SectionReviewContext);
  const blockId = useContext(CollectionBlockIdContext);
  if (!diff || !blockId || diff.designChanged) return null;
  return diff.fieldDiffs.find((c) => c.fieldKey === `${blockId}:${itemId}:${slot}`) ?? null;
}

// Returns ids of collection items that have at least one changed field,
// so accordion-style renderers can auto-expand them — otherwise
// a changed item's diff markup never mounts because it lives inside collapsed content.
export function useCollectionItemIdsWithChanges(): Set<string> {
  const diff = useContext(SectionReviewContext);
  const blockId = useContext(CollectionBlockIdContext);
  if (!diff || !blockId || diff.designChanged) return new Set();

  const ids = new Set<string>();
  for (const fieldDiff of diff.fieldDiffs) {
    if (!fieldDiff.hasChanges) continue;

    const [fieldBlockId, itemId] = fieldDiff.fieldKey.split(':');
    if (fieldBlockId !== blockId || !itemId) continue;
    ids.add(itemId);
  }
  return ids;
}
